'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { articleService, Article } from '@/lib/services/articleService';

const translations = {
  mn: {
    back: 'Буцах',
    tag: 'Mongtusa - Побтуса хуулийн мэдээ',
    title: 'Хуулийн мэдээ',
    subtitle: '법률 뉴스',
    description: 'Монгол болон Солонгосын шүүхийн шийдвэр, хууль тогтоомжийн шинэчлэл, хуулийн дүн шинжилгээ.',
    latestLabel: 'Сүүлийн мэдээнүүд',
    comingSoon: 'Удахгүй нийтлэлүүд гарах болно.',
    comingSoonSub: 'Хуулийн мэдээний агуулга бэлтгэгдэж байна.',
    copyright: 'Бүх эрх хуулиар хамгаалагдсан.',
    otherCategories: 'Бусад ангилалууд',
    searchPlaceholder: 'Гарчгаар хайх...',
    dateFrom: 'Эхлэх огноо',
    dateTo: 'Дуусах огноо',
    clearFilters: 'Шүүлтүүр арилгах',
    noResults: 'Хайлтад тохирох мэдээ олдсонгүй.',
    categories: [
      { id: 'financial', title: 'Санхүүгийн мэдээ', subtitle: 'финансовый', href: '/financial-news', icon: 'BanknotesIcon', accent: '#1a5c3a', bg: 'from-emerald-50 to-teal-50', border: 'border-emerald-200', hover: 'hover:border-emerald-400' },
      { id: 'other', title: 'Бусад мэдээ', subtitle: '기타', href: '/other-news', icon: 'NewspaperIcon', accent: '#1e3a5f', bg: 'from-blue-50 to-slate-50', border: 'border-blue-200', hover: 'hover:border-blue-400' },
    ],
  },
  kr: {
    back: '돌아가기',
    tag: '법률 & 정의',
    title: '법률 뉴스',
    subtitle: 'Хуулийн мэдээ',
    description: '몽골과 한국의 법원 판결, 법률 업데이트 및 법률 분석.',
    latestLabel: '최신 기사',
    comingSoon: '곧 기사가 게시될 예정입니다.',
    comingSoonSub: '법률 뉴스 콘텐츠를 준비 중입니다.',
    copyright: '모든 권리 보유.',
    otherCategories: '다른 카테고리',
    searchPlaceholder: '제목으로 검색...',
    dateFrom: '시작 날짜',
    dateTo: '종료 날짜',
    clearFilters: '필터 초기화',
    noResults: '검색 결과가 없습니다.',
    categories: [
      { id: 'financial', title: '금융 뉴스', subtitle: 'Санхүүгийн мэдээ', href: '/financial-news', icon: 'BanknotesIcon', accent: '#1a5c3a', bg: 'from-emerald-50 to-teal-50', border: 'border-emerald-200', hover: 'hover:border-emerald-400' },
      { id: 'other', title: '기타 뉴스', subtitle: 'Бусад мэдээ', href: '/other-news', icon: 'NewspaperIcon', accent: '#1e3a5f', bg: 'from-blue-50 to-slate-50', border: 'border-blue-200', hover: 'hover:border-blue-400' },
    ],
  },
};

const accent = '#8B4513';

export default function LegalNewsPage() {
  const [lang, setLang] = useState<'mn' | 'kr'>('mn');
  const [hovered, setHovered] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [articles, setArticles] = useState<Article[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem('preferredLanguage') as 'mn' | 'kr';
    if (saved === 'mn' || saved === 'kr') setLang(saved);
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as 'mn' | 'kr';
      if (detail === 'mn' || detail === 'kr') setLang(detail);
    };
    window.addEventListener('languageChange', handler);

    // Fetch articles and track views
    const trackViews = async () => {
      const allPublished = await articleService.getPublished();
      const legalArticles = allPublished.filter((a) => a.category === 'legal');
      setArticles(legalArticles);
      // Increment view count for each article in this category
      await Promise.all(legalArticles.map((a) => articleService.incrementViews(a.id)));
    };
    trackViews();

    return () => window.removeEventListener('languageChange', handler);
  }, []);

  const handleLangChange = (l: 'mn' | 'kr') => {
    setLang(l);
    localStorage.setItem('preferredLanguage', l);
    window.dispatchEvent(new CustomEvent('languageChange', { detail: l }));
  };

  const hasActiveFilters = searchQuery.trim() !== '' || dateFrom !== '' || dateTo !== '';

  const clearFilters = () => {
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
  };

  const filteredArticles = articles.filter((a) => {
    const title = lang === 'mn' ? a.titleMn : a.titleKr;
    const matchesSearch = searchQuery.trim() === '' || title.toLowerCase().includes(searchQuery.toLowerCase());
    const pubDate = a.publishedAt ? new Date(a.publishedAt) : null;
    const matchesFrom = dateFrom === '' || (pubDate && pubDate >= new Date(dateFrom));
    const matchesTo = dateTo === '' || (pubDate && pubDate <= new Date(dateTo + 'T23:59:59'));
    return matchesSearch && matchesFrom && matchesTo;
  });

  const t = translations[lang];

  return (
    <div className="min-h-screen bg-[#faf9f7] flex flex-col">
      {/* Header */}
      <header className="px-6 py-5 flex items-center justify-between border-b border-stone-200">
        <Link href="/homepage" className="flex items-center gap-3 cursor-pointer">
          <div className="w-9 h-9 bg-[#8B4513] rounded-lg flex items-center justify-center">
            <Icon name="ScaleIcon" size={20} className="text-white" />
          </div>
          <span className="text-lg font-semibold text-stone-800 tracking-tight">Mongtusa</span>
        </Link>
        <div className="flex items-center gap-1 bg-stone-100 rounded-full p-1">
          <button
            onClick={() => handleLangChange('mn')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${lang === 'mn' ? 'bg-[#8B4513] text-white shadow-sm' : 'text-stone-600 hover:bg-white'}`}
          >
            🇲🇳 Монгол
          </button>
          <button
            onClick={() => handleLangChange('kr')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${lang === 'kr' ? 'bg-[#8B4513] text-white shadow-sm' : 'text-stone-600 hover:bg-white'}`}
          >
            🇰🇷 한국어
          </button>
        </div>
      </header>

      <main className="flex-1 px-4 py-12 max-w-5xl mx-auto w-full">
        {/* Back */}

        {/* Hero Card */}
        <div className="rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-8 mb-10">
          <span
            className="inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full mb-4"
            style={{ backgroundColor: accent + '18', color: accent }}
          >
            {t.tag}
          </span>
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accent + '15' }}>
              <Icon name="ScaleIcon" size={32} style={{ color: accent }} />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-1">{t.title}</h1>
              <p className="text-sm font-mono text-stone-400 mb-3">{t.subtitle}</p>
              <p className="text-stone-600 leading-relaxed">{t.description}</p>
            </div>
          </div>
        </div>

        {/* Articles Section */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-stone-800 mb-6">{t.latestLabel}</h2>

          {/* Search & Date Filters */}
          <div className="rounded-2xl border border-stone-200 bg-white p-4 mb-4 flex flex-col sm:flex-row gap-3">
            {/* Search Bar */}
            <div className="relative flex-1">
              <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                style={{ '--tw-ring-color': accent + '40' } as React.CSSProperties}
              />
            </div>
            {/* Date From */}
            <div className="flex flex-col gap-1 min-w-[140px]">
              <label className="text-[10px] font-mono text-stone-400 uppercase tracking-wider px-1">{t.dateFrom}</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2 text-sm rounded-xl border border-stone-200 bg-stone-50 text-stone-700 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                style={{ '--tw-ring-color': accent + '40' } as React.CSSProperties}
              />
            </div>
            {/* Date To */}
            <div className="flex flex-col gap-1 min-w-[140px]">
              <label className="text-[10px] font-mono text-stone-400 uppercase tracking-wider px-1">{t.dateTo}</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2 text-sm rounded-xl border border-stone-200 bg-stone-50 text-stone-700 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                style={{ '--tw-ring-color': accent + '40' } as React.CSSProperties}
              />
            </div>
            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="self-end flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium rounded-xl border border-stone-200 text-stone-500 hover:bg-stone-50 hover:text-stone-700 transition-all whitespace-nowrap"
              >
                <Icon name="XMarkIcon" size={14} />
                {t.clearFilters}
              </button>
            )}
          </div>

          {/* Articles Placeholder */}
          {filteredArticles.length > 0 ? (
            <div className="space-y-4">
              {filteredArticles.map((article) => (
                <div key={article.id} className="rounded-2xl border border-stone-200 bg-white overflow-hidden hover:shadow-md transition-shadow">
                  {article.videoUrl && (
                    <div className="w-full overflow-hidden bg-black">
                      <video
                        src={article.videoUrl}
                        className="w-full max-h-72 object-contain"
                        controls
                        preload="metadata"
                      />
                    </div>
                  )}
                  {!article.videoUrl && article.imageUrl && (
                    <div className="w-full h-56 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={lang === 'mn' ? article.titleMn : article.titleKr}
                        className="w-full h-full object-cover"
                        crossOrigin="anonymous"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-stone-900 mb-2">
                          {lang === 'mn' ? article.titleMn : article.titleKr}
                        </h3>
                        <p className="text-sm text-stone-600 leading-relaxed line-clamp-3">
                          {lang === 'mn' ? article.contentMn : article.contentKr}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-4 pt-4 border-t border-stone-100">
                      <span className="text-xs text-stone-400 font-mono">
                        {article.publishedAt ? new Date(article.publishedAt).toLocaleDateString(lang === 'mn' ? 'mn-MN' : 'ko-KR') : ''}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-stone-400">
                        <Icon name="EyeIcon" size={12} />
                        {article.views}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-stone-200 bg-white p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
                <Icon name="ScaleIcon" size={28} style={{ color: accent }} />
              </div>
              {hasActiveFilters ? (
                <p className="text-stone-500 font-medium">{t.noResults}</p>
              ) : (
                <>
                  <p className="text-stone-700 font-medium mb-2">{t.comingSoon}</p>
                  <p className="text-sm text-stone-400">{t.comingSoonSub}</p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Other Categories */}
        <div>
          <h2 className="text-xl font-bold text-stone-800 mb-6">{t.otherCategories}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {t.categories.map((cat) => (
              <Link
                key={cat.id}
                href={cat.href}
                onMouseEnter={() => setHovered(cat.id)}
                onMouseLeave={() => setHovered(null)}
                className={`group rounded-2xl border-2 ${cat.border} ${cat.hover} bg-gradient-to-br ${cat.bg} p-6 flex flex-col gap-3 transition-all duration-300 ${hovered === cat.id ? 'shadow-lg -translate-y-1' : 'shadow-sm'}`}
                style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: cat.accent + '15' }}>
                  <Icon name={cat.icon as any} size={22} style={{ color: cat.accent }} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-stone-900">{cat.title}</h3>
                  <p className="text-xs font-mono text-stone-400">{cat.subtitle}</p>
                </div>
                <div className="flex items-center gap-1 text-sm font-medium" style={{ color: cat.accent }}>
                  <span>{lang === 'mn' ? 'Үзэх' : '탐색'}</span>
                  <Icon name="ArrowRightIcon" size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 py-5 border-t border-stone-100 flex items-center justify-between mt-8">
        <p className="text-xs text-stone-400">© {new Date().getFullYear()} Mongtusa. {t.copyright}</p>
        <Link href="/login" className="font-mono text-[10px] text-stone-300 hover:text-stone-500 transition-colors">v2.1.0</Link>
      </footer>
    </div>
  );
}
